import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getSeatableReservations } from "../../../../../api/reservations";
import { useReservationsLocked } from "../../../../../hooks/useEdgeMode";
import { useAppSelector } from "../../../../../store/hooks";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useOrderingMessage } from "../../useOrderingMessage";
import { SeatReservationModal } from "../seat/SeatReservationModal";
import { SEATABLE_KEY, useSeatFromOrdering } from "../seat/useSeatReservations";
import type { SeatModalInitial } from "../seat/useSeatModalState";
import { voiceSeatPlan } from "./voiceSeatPlan";

// Правото на „Настани" от Маркиране (BE `SEAT_PERMISSION`) — гласът не дава повече.
const SEAT_PERMISSION = "seat_reservations";

// `cancelled` — гласовият диалог вече не е на това слушане (затворен,
// „Отново", демонтаж): без съобщение и без промяна в диалога.
export type VoiceSeatOutcome = { kind: "done" } | { kind: "error"; text: string } | { kind: "cancelled" };

const CANCELLED: VoiceSeatOutcome = { kind: "cancelled" };

/**
 * Гласово „Настани резервацията на Здравко" (`seatGuestName` от
 * `POST /voice/parse-order`): пресните данни на „Настани" → `voiceSeatPlan` →
 * ясен случай настанява направо (`POST …/seat-from-ordering`, като бутона),
 * иначе отваря модала „Настани" на резервацията за ръчно довършване.
 * Без правото `seat_reservations` — `onSeatReservation` е `null` и гласовият
 * модал казва, че няма право. Модалът живее тук, не в лентата под масите.
 *
 * `isCurrent` (от гласовия диалог) се сверява след всяко await: остарял
 * резултат не настанява и не отваря „Настани". Вече ИЗПРАТЕНОТО настаняване
 * не се отменя — съобщението за успех излиза (кешът се опреснява от
 * мутацията), но `onSeated` (избор на сметката + менюто) само ако диалогът е
 * още на същото слушане: иначе сервитьорът вече прави друго и смяната на
 * избраната сметка би пратила поръчката му в чужда маса.
 */
export const useVoiceSeat = ({ onSeated }: { onSeated: (billId: number) => void }) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const queryClient = useQueryClient();
  const seatMutation = useSeatFromOrdering();
  const locked = useReservationsLocked();
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canSeat = permissions.some((permission) => permission.name === SEAT_PERMISSION);
  const [modalInitial, setModalInitial] = useState<SeatModalInitial | null>(null);

  const seatBySpokenName = useCallback(
    async (spokenName: string, isCurrent: () => boolean): Promise<VoiceSeatOutcome> => {
      if (locked) return { kind: "error", text: t("edge.unavailableOffline") };
      let data;
      try {
        data = await queryClient.fetchQuery({ queryKey: SEATABLE_KEY, queryFn: getSeatableReservations, staleTime: 0 });
      } catch (error: unknown) {
        if (!isCurrent()) return CANCELLED;
        return { kind: "error", text: getApiErrorMessage(error, t("seatReservation.loadError")) };
      }
      if (!isCurrent()) return CANCELLED;
      const plan = voiceSeatPlan(data, spokenName, Date.now());
      if (plan.kind === "notFound") {
        return { kind: "error", text: t("ordering.voiceSeatNotFound", { name: spokenName.trim() }) };
      }
      if (plan.kind === "manual") {
        setModalInitial({ query: plan.query, reservationId: plan.reservationId });
        return { kind: "done" };
      }
      try {
        const result = await seatMutation.mutateAsync({
          id: plan.reservation.id,
          target: plan.target,
          separateBills: false,
        });
        // Настаняването е станало — съобщението излиза и при затворен диалог.
        message.success(
          t("seatReservation.success", { guest: plan.reservation.guestName, table: result.tableNames[0] ?? "" }),
        );
        if (!isCurrent()) return CANCELLED;
        if (result.billId !== null) onSeated(result.billId);
        return { kind: "done" };
      } catch (error: unknown) {
        // Неуспешно настаняване не е променило нищо — затвореният диалог мълчи.
        if (!isCurrent()) return CANCELLED;
        return { kind: "error", text: getApiErrorMessage(error, t("common.error")) };
      }
    },
    [locked, queryClient, seatMutation, message, onSeated, t],
  );

  const seatModal = canSeat ? (
    <SeatReservationModal
      open={modalInitial !== null}
      onClose={() => setModalInitial(null)}
      onSeated={onSeated}
      initial={modalInitial}
    />
  ) : null;

  return {
    onSeatReservation: canSeat ? seatBySpokenName : null,
    seatModal,
    seatModalOpen: modalInitial !== null,
  };
};
